import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ExternalLink, Mic, Video, Presentation } from 'lucide-react';
import axios from 'axios';
import { SEO } from '../components/SEO';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', { 
    day: 'numeric',
    month: 'long', 
    year: 'numeric' 
  });
};

const getMediaIcon = (type) => {
  switch (type) {
    case 'Podcast':
      return <Mic size={20} />;
    case 'Interview':
      return <Video size={20} />;
    case 'Speaking':
      return <Presentation size={20} />;
    default:
      return <ExternalLink size={20} />;
  }
};

const HeroSection = () => {
  return (
    <section 
      className="min-h-[35vh] flex items-center bg-[#faf9f7]"
      data-testid="media-speaking-hero"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        <h1 
          className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold italic text-[#1a1a1a] mb-6 animate-fade-in-up"
          data-testid="media-speaking-hero-title"
        >
          Media & Speaking
        </h1>
        <p 
          className="font-dm-sans text-lg md:text-xl text-[#4a4744] leading-relaxed max-w-3xl animate-fade-in-up animate-delay-200"
          data-testid="media-speaking-hero-subtitle"
        >
          Interviews, podcasts, conference presentations, and thought leadership on organisational transformation.
        </p>
      </div>
    </section>
  );
};

const MediaCard = ({ item, index }) => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <a 
      ref={ref}
      href={item.link_url}
      target="_blank"
      rel="noopener noreferrer"
      className={`block bg-white border border-[#e5e2dc] p-6 transition-all duration-500 hover:border-[#d4782c] hover:shadow-md ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
      data-testid={`media-card-${item.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 bg-[#faf9f7] flex items-center justify-center text-[#d4782c]">
          {getMediaIcon(item.media_type)}
        </div>
        <div className="flex-grow">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[#d4782c] font-dm-sans text-xs font-semibold uppercase tracking-wider">
              {item.media_type}
            </span>
            <span className="text-[#4a4744] font-dm-sans text-xs">
              {formatDate(item.date)}
            </span>
          </div>
          <h3 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-1 leading-tight">
            {item.title}
          </h3>
          <p className="text-[#d4782c] font-dm-sans text-sm font-medium mb-2">
            {item.source}
          </p>
          {item.description && (
            <p className="text-[#4a4744] text-sm leading-relaxed">
              {item.description}
            </p>
          )}
        </div>
      </div>
    </a>
  );
};

const MediaSection = () => {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch media items on load
  useEffect(() => {
    const fetchMedia = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/media`);
        setMediaItems(response.data);
      } catch (error) {
        logger.error('Error fetching media:', error);
        setMediaItems([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMedia();
  }, []);

  return (
    <section className="pt-12 pb-20 md:pt-16 md:pb-24 bg-white" data-testid="media-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-[#d4782c] font-dm-sans font-semibold text-sm uppercase tracking-wider mb-3">
            MEDIA & APPEARANCES
          </p>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a]">
            Interviews, Podcasts & Speaking
          </h2>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-[#e5e2dc] p-6 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#e5e2dc] rounded"></div>
                  <div className="flex-grow">
                    <div className="h-3 bg-[#e5e2dc] rounded w-1/3 mb-2"></div>
                    <div className="h-5 bg-[#e5e2dc] rounded w-full mb-2"></div>
                    <div className="h-3 bg-[#e5e2dc] rounded w-1/2"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : mediaItems.length === 0 ? (
          <p className="text-center text-[#4a4744]">No media appearances yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mediaItems.map((item, index) => (
              <MediaCard key={item.id} item={item} index={index} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const SpeakingSection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-20 md:py-24 bg-[#faf9f7]" data-testid="speaking-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`text-center transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#d4782c] font-dm-sans font-semibold text-sm uppercase tracking-wider mb-3">
            SPEAKING
          </p>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6">
            Keynotes & Conference Speaking
          </h2>
          <p className="text-[#4a4744] text-lg leading-relaxed mb-10 max-w-3xl mx-auto">
            Nadir Khan is available for keynote presentations, panel discussions, and conference speaking engagements on organisational transformation, operating model design, and the Goldilocks Zone framework.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-block"
            data-testid="speaking-enquiry-btn"
          >
            Enquire About Speaking
          </Link>
        </div>
      </div>
    </section>
  );
};

const CTASection = () => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <section className="py-20 md:py-24 bg-white" data-testid="cta-section">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div 
          ref={ref}
          className={`text-center transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6">
            Let's start a conversation
          </h2>
          <p className="text-[#4a4744] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Whether you're interested in our transformation services or exploring a speaking engagement, we'd love to hear from you.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-block"
            data-testid="cta-contact-btn"
          >
            Get in Touch
          </Link>
        </div>
      </div>
    </section>
  );
};

export default function MediaSpeakingPage() {
  return (
    <Layout>
      <SEO 
        title="Media & Speaking"
        path="/media-speaking"
        description="Interviews, podcasts, conference presentations, and thought leadership on organisational transformation from Nadir Khan and Agilitie."
      />
      <HeroSection />
      <MediaSection />
      <SpeakingSection />
    </Layout>
  );
}
